import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

/**
 * ConfirmDialog Context
 * Provides Promise-based confirmation dialog API
 */
const ConfirmDialogContext = createContext(null);

/**
 * Hook to access confirmation dialog
 * @returns {{ showConfirm: (options) => Promise<boolean> }}
 */
export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return context;
};

/**
 * ConfirmDialog Provider
 * Wraps app to provide confirmation dialog functionality
 */
export const ConfirmDialogProvider = ({ children }) => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default' // 'default' | 'danger'
  });

  const resolveRef = useRef(null);
  const modalRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const confirmButtonRef = useRef(null);
  // Snapshot of the element that had focus before the dialog opened, so we
  // can restore it on close (WCAG 2.4.3 Focus Order).
  const previousFocusRef = useRef(null);

  /**
   * Show confirmation dialog
   * @param {Object} options - Dialog configuration
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Dialog message
   * @param {string} [options.confirmText='Confirm'] - Confirm button text
   * @param {string} [options.cancelText='Cancel'] - Cancel button text
   * @param {string} [options.variant='default'] - Button variant ('default' | 'danger')
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   */
  const showConfirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({
        isOpen: true,
        title: options.title || '',
        message: options.message || '',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'default'
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
    setDialogState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleCancel = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setDialogState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Escape key handler
  useEffect(() => {
    if (!dialogState.isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [dialogState.isOpen, handleCancel]);

  // Prevent body scroll when dialog is open
  useBodyScrollLock(dialogState.isOpen);

  // Focus management (WCAG 2.4.3 + 2.1.2 — Focus Order + No Keyboard Trap).
  // On open: stash the previously-focused element and move focus to Cancel
  // (the non-destructive default). On close: restore focus to the opener.
  useEffect(() => {
    if (dialogState.isOpen) {
      previousFocusRef.current = document.activeElement;
      // Defer to next tick so the buttons are actually in the DOM.
      const id = setTimeout(() => cancelButtonRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
    return undefined;
  }, [dialogState.isOpen]);

  // Tab/Shift+Tab focus trap. With only two focusable buttons this is a
  // ping-pong: forward Tab from confirm → cancel; backward from cancel →
  // confirm. Browser default takes care of the inner case.
  const handleTabTrap = useCallback((event) => {
    if (event.key !== 'Tab') return;
    const cancel = cancelButtonRef.current;
    const confirm = confirmButtonRef.current;
    if (!cancel || !confirm) return;
    if (!event.shiftKey && document.activeElement === confirm) {
      event.preventDefault();
      cancel.focus();
    } else if (event.shiftKey && document.activeElement === cancel) {
      event.preventDefault();
      confirm.focus();
    }
  }, []);

  return (
    <ConfirmDialogContext.Provider value={{ showConfirm }}>
      {children}
      {dialogState.isOpen && (
        <div className="confirm-dialog-overlay">
          <div
            className="confirm-dialog-container"
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogState.title ? 'confirm-dialog-title' : undefined}
            aria-describedby={dialogState.message ? 'confirm-dialog-message' : undefined}
            onKeyDown={handleTabTrap}
          >
            {/* Title */}
            {dialogState.title && (
              <div className="confirm-dialog-title" id="confirm-dialog-title">
                {dialogState.title}
              </div>
            )}

            {/* Message */}
            {dialogState.message && (
              <div className="confirm-dialog-message" id="confirm-dialog-message">
                {dialogState.message}
              </div>
            )}

            {/* Actions */}
            <div className="confirm-dialog-actions">
              <button
                ref={cancelButtonRef}
                className="confirm-dialog-button confirm-dialog-button-cancel"
                onClick={handleCancel}
                type="button"
              >
                {dialogState.cancelText}
              </button>
              <button
                ref={confirmButtonRef}
                className={`confirm-dialog-button confirm-dialog-button-confirm confirm-dialog-button-${dialogState.variant}`}
                onClick={handleConfirm}
                type="button"
              >
                {dialogState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmDialogContext.Provider>
  );
};

export default ConfirmDialogProvider;
